//#region IMPORTS
import { PhotoIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { useTranslation } from "react-i18next";
//#endregion

//#region INTERFACE
interface Props {
  // WoodWing thumbnail link; undefined while unknown or when there is none.
  src?: string;
  alt: string;
  onOpen: () => void;
}
//#endregion

export default function WeekOrderItemThumbnail({ src, alt, onOpen }: Props) {
  //#region SETUP
  const { t } = useTranslation();
  // Remember *which* link failed, so a fresh link (after the cache expired)
  // gets a new chance instead of staying on the placeholder.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = !!src && src !== failedSrc;
  //#endregion

  //#region RENDER
  // A fixed 128px square keeps the column aligned, but the square itself is
  // invisible: the image keeps its own ratio inside it (portrait or landscape,
  // never cropped) and carries the hairline border itself, so there are no
  // grey bands around a photo that doesn't fill the box. 128 because the
  // WoodWing thumbnail is 256px on its long side — the largest size that stays
  // sharp on a retina screen.
  if (!showImage)
    return (
      <div
        className="h-32 w-32 shrink-0 flex items-center justify-center rounded-lg bg-gray-100"
        aria-hidden="true"
      >
        <PhotoIcon className="h-10 w-10 text-gray-300" />
      </div>
    );

  return (
    <button
      type="button"
      onClick={onOpen}
      title={t("rituals.showLargerImage", "Show larger image")}
      className="h-32 w-32 shrink-0 flex items-center justify-center transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-600"
    >
      <img
        src={src}
        alt={alt}
        onError={() => setFailedSrc(src ?? null)}
        className="h-auto w-auto max-h-full max-w-full rounded-md ring-1 ring-gray-200"
      />
    </button>
  );
  //#endregion
}
